import { computed, ref, type Component, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { Layers2 } from "lucide-vue-next";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useQuestionCardTypes } from "@/utils/composables/questionCard";
import type { Card, ResultsCard } from "@/types";
import { CARD_DESCRIPTIONS } from "@/utils/constants/cardTypes";
import { isCodificationCard, isOpenRecodeCard } from "@utils/hiddenCardQuestionType";
import { cardSubtype, cardTypeMeta } from "@/components/Survey/ResultsV2/constants";

export function useCardTypeIdentity(card: Ref<ResultsCard | null>) {
  const { availableCards } = storeToRefs(useResultsV2Store());
  const { determineIcon } = useQuestionCardTypes(ref({}));

  const questionMeta = computed(() =>
    ["question", "recode"].includes(card.value?.card_type || "")
      ? (availableCards.value as Card[]).find((c) => c.id === card.value?.card_id) || null
      : null,
  );
  const isOpenRecode = computed(() => isOpenRecodeCard(questionMeta.value));
  const isCodification = computed(() => isCodificationCard(questionMeta.value));

  const label = computed(() => {
    if (isOpenRecode.value) return "AI recode (interviews)";
    if (isCodification.value) return "Open end codification";
    const type = questionMeta.value?.type;
    if (type) return CARD_DESCRIPTIONS[type] || type;
    return cardTypeMeta(cardSubtype(card.value) || card.value?.card_type)?.label || "";
  });

  const icon = computed<Component | null>(() => {
    if (isCodification.value) return Layers2;
    if (questionMeta.value?.type) return determineIcon(questionMeta.value.type);
    return cardTypeMeta(cardSubtype(card.value) || card.value?.card_type)?.icon || null;
  });

  return { icon, label };
}
